import { Button } from '@/components/ui/Button';
import {
  AlertTriangle,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  ImageOff,
  Lightbulb,
  X,
  ZoomIn,
} from 'lucide-react';
import { useState } from 'react';

interface ProofViewerProps {
  proofUrl: string | null | undefined;
  purchaseId?: string; // Reservado para futuras funcionalidades
}

export function ProofViewer({ proofUrl }: ProofViewerProps) {
  const [isZoomed, setIsZoomed] = useState(false);
  const [imageError, setImageError] = useState(false);

  if (!proofUrl) {
    return (
      <div className="bg-gray-50 rounded-xl border-2 border-dashed border-gray-200 p-8 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
          <ImageOff className="w-8 h-8 text-gray-400" />
        </div>
        <h4 className="font-medium text-gray-700 mb-1">Sin comprobante</h4>
        <p className="text-sm text-gray-500">El cliente aún no ha subido su comprobante de pago</p>
        <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-100">
          <p className="text-xs text-amber-700 flex items-start gap-2">
            <Lightbulb className="w-4 h-4 mt-0.5" />
            <span>Puedes contactar al cliente para recordarle que suba su comprobante</span>
          </p>
        </div>
      </div>
    );
  }

  const isPdf = proofUrl.toLowerCase().endsWith('.pdf');

  if (isPdf) {
    return (
      <div className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden">
        <div className="p-4 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-red-600" />
            <span className="font-medium text-gray-700">Comprobante PDF</span>
          </div>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => window.open(proofUrl, '_blank')}>
              <ExternalLink className="w-4 h-4 mr-1" />
              Abrir PDF
            </Button>
          </div>
        </div>

        {/* Preview iframe para PDF */}
        <div className="p-4 bg-white">
          <div className="flex items-start gap-2 text-sm text-slate-600">
            <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600" />
            <p>
              La vista previa del PDF está desactivada para reducir consumo de datos. Usa “Abrir
              PDF” para verlo.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Imagen
  return (
    <>
      <div className="relative bg-gray-900 rounded-xl overflow-hidden group">
        {/* Toolbar flotante */}
        <div className="absolute top-3 right-3 z-10 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => setIsZoomed(true)}
            className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg backdrop-blur-sm transition-colors"
            title="Ver en grande"
          >
            <ZoomIn className="w-5 h-5" />
          </button>
          <a
            href={proofUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-2 bg-black/50 hover:bg-black/70 text-white rounded-lg backdrop-blur-sm transition-colors"
            title="Abrir en nueva pestaña"
          >
            <ExternalLink className="w-5 h-5" />
          </a>
        </div>

        {/* Imagen del comprobante */}
        {!imageError ? (
          <img
            src={proofUrl}
            alt="Comprobante de pago"
            className="w-full h-auto max-h-[400px] object-contain cursor-zoom-in"
            onClick={() => setIsZoomed(true)}
            onError={() => setImageError(true)}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="p-8 text-center text-gray-400">
            <AlertTriangle className="w-12 h-12 mx-auto mb-2" />
            <p>Error al cargar la imagen</p>
            <a
              href={proofUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:underline text-sm mt-2 inline-block"
            >
              Abrir enlace directo
            </a>
          </div>
        )}

        {/* Indicador de tipo de archivo */}
        <div className="absolute bottom-3 left-3 px-2 py-1 bg-black/50 text-white text-xs rounded backdrop-blur-sm">
          <span className="inline-flex items-center gap-1">
            <ImageIcon className="w-3.5 h-3.5" />
            Imagen
          </span>
        </div>
      </div>

      {/* Modal de zoom */}
      {isZoomed && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setIsZoomed(false)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
            onClick={() => setIsZoomed(false)}
          >
            <X className="w-8 h-8" />
          </button>
          <img
            src={proofUrl}
            alt="Comprobante de pago (ampliado)"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
            loading="lazy"
            decoding="async"
          />
        </div>
      )}
    </>
  );
}
